import { Input, Modal, Row, Switch } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { updateEmployeeAction } from 'redux/action/employeesTableAction';
import { checkPermission, toastText } from 'utils/utils';
import { CloseSvg } from 'utils/svgs';
import styles from './index.module.scss';

interface Props {
	open: boolean;
	employee: {
		id: string;
		fullName: string;
		isActiveInApp: boolean;
	};
	companyId: string;
	// Receives the saved values so the EC grid can patch the row in place
	// (no re-fetch — a re-fetch with includeInactive=false would drop a
	// newly-inactive employee before Item 9 runs). See fix pass 3.
	onSave: (updated: { fullName: string; isActiveInApp: boolean }) => void;
	onCancel: () => void;
}

// ENG-363 (Item 8): edit an employee's name and active/inactive status from the
// Employee Cost grid in a single modal. Mirrors EmployeeConfigModal's chrome
// (custom header + footer buttons). On save it dispatches the shared
// updateEmployeeAction (PUT /employees/:id) and asks the parent to refresh.
export const EmployeeEditModal = ({
	open,
	employee,
	companyId,
	onSave,
	onCancel,
}: Props) => {
	const dispatch = useDispatch<AppDispatch>();

	const [draftName, setDraftName] = useState<string>('');
	const [draftActive, setDraftActive] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	// Re-seed the draft whenever a different employee is opened.
	useEffect(() => {
		if (employee) {
			setDraftName(employee.fullName ?? '');
			setDraftActive(employee.isActiveInApp !== false);
		}
	}, [employee]);

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	// The backend requires Employees:edit for name/status changes.
	const isEditPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Employees',
		permission: ['edit'],
	});

	const handleSave = async () => {
		const trimmed = draftName.trim();
		if (!trimmed) {
			toastText('Employee name cannot be empty', 'error');
			return;
		}

		try {
			setIsSaving(true);
			await dispatch(
				updateEmployeeAction({
					companyId,
					employeeId: employee.id,
					fullName: trimmed,
					isActiveInApp: draftActive,
				})
			).unwrap();
			toastText('Employee updated', 'success');
			onSave({ fullName: trimmed, isActiveInApp: draftActive });
		} catch (error: any) {
			toastText(error?.message || 'Failed to update employee', 'error');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			closable={false}
			style={{ borderRadius: 0 }}
			footer={[]}
			width={600}
			centered
		>
			<div>
				<Row className={styles['edit-modal__top']}>
					<div className={styles['edit-modal__top-title']}>
						<b>Edit Employee</b>
					</div>
					<div
						className={styles['edit-modal__top-close']}
						onClick={onCancel}
					>
						<CloseSvg />
					</div>
				</Row>
			</div>
			<div className={styles['edit-modal__main']}>
				<div className={styles['edit-modal__field']}>
					<label className={styles['edit-modal__label']}>Employee Name</label>
					<Input
						value={draftName}
						disabled={isSaving}
						onChange={(e) => setDraftName(e.target.value)}
						onPressEnter={handleSave}
					/>
				</div>
				<div className={styles['edit-modal__field']}>
					<label className={styles['edit-modal__label']}>Active</label>
					<Switch
						checked={draftActive}
						disabled={isSaving}
						onChange={(flag) => setDraftActive(flag)}
					/>
				</div>
				<div className={styles['edit-modal__buttons']}>
					<button
						className={styles['edit-modal__buttons-cancel']}
						onClick={onCancel}
						disabled={isSaving}
					>
						Cancel
					</button>
					{isEditPermission && (
						<button
							className={`${styles['edit-modal__buttons-save']} ${
								isSaving && 'pointer-event-none'
							}`}
							onClick={handleSave}
							disabled={isSaving}
						>
							{isSaving ? (
								<img src="/assets/gifs/loading-black.gif" height={40} />
							) : (
								'Save'
							)}
						</button>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default EmployeeEditModal;
